import React from 'react';
import {
    Spinner,
    Page,
    List,
    Layout,
    Card,
    Button,
    PageActions,
    FormLayout,
    Tag,
    TextField
} from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import { apiFetch as fetch } from '../../common/Helpers';
import { getRuleDefinitionByType } from '../../rules/TagRuleDefinitions';
import {
    RuleNameFormElement,
    TriggerFormElement
} from '../tag-rules/rules/formElements/FormElements';
import SelectProductsModal from "../../components/SelectProductsModal"
import {
    AutoTagProductsByStatusSettings,
    AutoTagProductsByVendorSettings,
    TagProductsOnCreationSettings,
    AutoTagProductsByTypeSettings,
    TagProductsOnSaleSettings,
    AutoTagProductsByVariantOptionSettings,
    AutoTagOrdersByLocationSettings,
    TagOrdersByVendorSettings,
    TagNewProductsSettings,
    TagProductsByKeywordsSettings,
    TagCustomersByPurchasedProductsSettings,
    TagProductsWhenTagExistsSettings,
    UpdateProductMetafieldFromDescriptionFieldPatternSettings,
    UpdateProductMetafieldFromTagGroupRuleSettings,
    UpdateMultipleProductMetafieldsFromTagGroupSettings,
    UpdateProductVariantMetafieldFromVariantOptionMappingSettings,
    UpdateProductVariantMetafieldFromVariantOptionSettings,
    UpdateProductMetafieldFromVariantOptionSettings,
    TagProductsWhenMultipleTagsExistSettings,
    UpdateProductMetafieldFromTagMappingSettings
} from './rules/ruleFields/RuleFields'
import { TagRulePreviewArea } from './TagRulePreviewArea'
import { RequiredPermissionsWarning } from '../../common/RequiredPermissionsWarning';

class TagRulePage extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            loading: true,
            loadingPreview: false,
            modalActive: false,
            showingAdvancedSettings: false,
            products: [],
            selectedProducts: [],
            resourcePreviews: [],
            rule: null,
            ruleErrors: {
            }
        };

        const { params } = this.props.match;

        if (params.id) {
            this.editId = params.id
        }

        if (params.ruleType) {
            this.ruleDefinition = getRuleDefinitionByType(params.ruleType);
            this.ruleValidator = this.ruleDefinition.createValidator(this.handleOnAsyncError);
            this.state.rule = this.ruleDefinition.getDefaultRuleState();
            this.state.ruleErrors = this.ruleValidator.validate(this.state.rule);
        }
    }

    initRule = (ruleType) => {
        this.ruleDefinition = getRuleDefinitionByType(ruleType);
        this.ruleValidator = this.ruleDefinition.createValidator(this.handleOnAsyncError);
        const newRule = this.ruleDefinition.getDefaultRuleState();

        this.setState({
            rule: newRule,
            ruleErrors: this.ruleValidator.validate(newRule)
        });
    }

    render() {

        if (this.state.loading) {
            return <Spinner accessibilityLabel="Loading" size="large" color="teal" />
        }

        if (this.ruleDefinition == null) {
            return <span>Unknown rule!</span>
        }

        var title = `Create Rule - ${this.ruleDefinition.title}`;
        var actionTitle = "Create";

        if (this.editId != null) {
            title = `Update Rule - ${this.ruleDefinition.title}`;
            actionTitle = "Update";
        }

        var advancedSettingsAction = [{
            content: 'Show', onClick: () => {
                this.setState({
                    showingAdvancedSettings: !this.state.showingAdvancedSettings
                })
            }
        }];

        if (this.state.showingAdvancedSettings) {
            advancedSettingsAction = [{
                content: 'Hide', onClick: () => {
                    this.setState({
                        showingAdvancedSettings: !this.state.showingAdvancedSettings
                    })
                }
            }];
        }

        const canSave = !this.hasErrors(this.state.ruleErrors);
        const additional = this.ruleDefinition.additional.map(_ => <List.Item>{_}</List.Item>);
        const primaryAction = { content: actionTitle, onAction: this.handleSaveOrCreate, disabled: !canSave }
        // Preview sidebar is temporarily disabled - see the commented-out Layout.Section below.
        // const supportsPreview = this.ruleDefinition.allowPreview !== false;

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Tag Rules - {this.ruleDefinition.title}</title>
                </Helmet>
                <Page
                    title={title}
                    primaryAction={primaryAction}
                    breadcrumbs={[{ content: 'Tag Rules', url: '/tag-rules' }]}
                >
                    {this.state.modalActive && <SelectProductsModal
                        active={this.state.modalActive}
                        onCancel={this.onModalCancel}
                        onDone={this.onModalDone}
                        onQueryProducts={this.onQueryProducts}
                        selectedProducts={this.state.selectedProducts}
                        products={this.state.products}
                    />}
                    <Layout fullWidth>
                        <Layout.Section fullWidth>
                            <RequiredPermissionsWarning requiredPermissions={this.ruleDefinition.dataPermissions} />
                        </Layout.Section>
                        <Layout.Section>
                            <Card>
                                <Card.Section>
                                    <p><b>{this.ruleDefinition.description}</b></p>
                                    <br />
                                    <div>
                                        <p><b>Example:</b></p>
                                        <p>{this.ruleDefinition.example}</p>
                                    </div>
                                    {additional.length > 0 && <div>
                                        <br />
                                        <p><b>Additional:</b></p>
                                        <List type="bullet">
                                            {additional}
                                        </List>
                                    </div>}
                                </Card.Section>
                                <Card.Section>
                                    <FormLayout>
                                        <RuleNameFormElement
                                            rule={this.state.rule}
                                            ruleErrors={this.state.ruleErrors}
                                            onFieldValueChange={this.handleOnRuleFieldValueChange} />
                                    </FormLayout>
                                </Card.Section>
                                {this.renderRuleFields(this.ruleDefinition.ruleType, this.state.rule, this.state.ruleErrors)}
                                <Card.Section>
                                    <FormLayout>
                                        <TriggerFormElement
                                            rule={this.state.rule}
                                            ruleErrors={this.state.ruleErrors}
                                            onFieldValueChange={this.handleOnRuleFieldValueChange} />
                                    </FormLayout>
                                </Card.Section>
                            </Card>
                            <Card title="Advanced Settings" actions={advancedSettingsAction}>
                                {this.state.showingAdvancedSettings &&
                                    <Card.Section title="Rule Ignore Settings">
                                        <p>Ignore Tag: <Tag>{this.state.rule.exclusionTag}</Tag></p>
                                        <div>
                                            <TextField
                                                label="Ignored Items"
                                                value={this.state.rule.excludedIds}
                                                onChange={this.handleOnRuleExcludedIdsChange}
                                                multiline={5}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </Card.Section>
                                }
                                <br/>
                            </Card>
                        </Layout.Section>
                        {/* Preview sidebar temporarily disabled to free up screen space - keep the code, we may bring it back.
                        {supportsPreview &&
                            <Layout.Section oneThird>
                                <Card title="Preview" actions={[
                                    { content: 'Clear', onAction: this.onClearPreview },
                                    { content: 'Refresh', onAction: this.onRefreshPreview }
                                ]}>
                                    <Card.Section>
                                        <p>Select at least one product to preview how rule might affect tags.</p>
                                        <br />
                                        <Button disabled={!canSave} onClick={this.handleSelectTags}>Select Products</Button>
                                    </Card.Section>
                                    {this.renderPreviewArea()}
                                </Card>
                            </Layout.Section>
                        }
                        */}
                    </Layout>
                    <br />
                    <PageActions
                        primaryAction={primaryAction}
                    />
                </Page>
            </div>
        );
    }

    renderRuleFields = (ruleType, rule, ruleErrors) => {

        if (ruleType == null || rule == null) {
            return null;
        }

        if (ruleType === "TagProductsByStatus") {
            return <AutoTagProductsByStatusSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsByVendor") {
            return <AutoTagProductsByVendorSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsOnCreation") {
            return <TagProductsOnCreationSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsByType") {
            return <AutoTagProductsByTypeSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsOnSale") {
            return <TagProductsOnSaleSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsByVariantOption") {
            return <AutoTagProductsByVariantOptionSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsByVariantOption") {
            return <AutoTagProductsByVariantOptionSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagOrdersByLocation") {
            return <AutoTagOrdersByLocationSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagOrdersByVendor") {
            return <TagOrdersByVendorSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagNewProducts") {
            return <TagNewProductsSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsByKeywords") {
            return <TagProductsByKeywordsSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagCustomersByPurchasedProducts") {
            return <TagCustomersByPurchasedProductsSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsWhenTagExists") {
            return <TagProductsWhenTagExistsSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "TagProductsWhenMultipleTagsExist") {
            return <TagProductsWhenMultipleTagsExistSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductMetafieldFromDescriptionFieldPattern") {
            return <UpdateProductMetafieldFromDescriptionFieldPatternSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductMetafieldFromTagGroup") {
            return <UpdateProductMetafieldFromTagGroupRuleSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateMultipleProductMetafieldsFromTagGroup") {
            return <UpdateMultipleProductMetafieldsFromTagGroupSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductVariantMetafieldFromVariantOptionMapping") {
            return <UpdateProductVariantMetafieldFromVariantOptionMappingSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductVariantMetafieldFromVariantOption") {
            return <UpdateProductVariantMetafieldFromVariantOptionSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductMetafieldFromVariantOption") {
            return <UpdateProductMetafieldFromVariantOptionSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        if (ruleType === "UpdateProductMetafieldFromTagMapping") {
            return <UpdateProductMetafieldFromTagMappingSettings
                settings={rule.settings}
                settingsErrors={ruleErrors}
                onSettingsChange={this.handleOnSettingsChange}
            />
        }

        return <span>renderRuleFields for {ruleType} not found</span>
    }

    handleOnRuleFieldValueChange = (fieldName, fieldValue) => {

        var rule = this.state.rule;
        rule[fieldName] = fieldValue;
        const errors = this.ruleValidator.validate(rule);

        this.setState({
            rule: rule,
            ruleErrors: errors
        }, () => this.loadPreview());
    }

    handleOnRuleExcludedIdsChange = (value) => {

        var rule = this.state.rule;
        rule.excludedIds = value;
        const errors = this.ruleValidator.validate(rule);

        this.setState({
            rule: rule,
            ruleErrors: errors
        });
    }

    handleOnSettingsChange = (value) => {

        var rule = this.state.rule;
        rule.settings = value;
        const errors = this.ruleValidator.validate(rule);

        this.setState({
            rule: rule,
            ruleErrors: errors
        }, () => this.loadPreview());
    }

    handleOnAsyncError = (errors) => {
        this.setState({
            ruleErrors: errors
        });
    }

    onQueryProducts = (searchString) => {

        fetch(`api/products/matching?queryString=${searchString}`, {
            method: 'GET'
        })
            .then(response => response.json())
            .then(data => this.setState({
                products: data,
            }));
    }

    handleSelectTags = () => {
        this.setState({
            modalActive: true
        });
    }

    onModalCancel = () => {
        this.setState({
            modalActive: false
        });
    }

    onModalDone = (selectedProducts) => {
        this.setState({
            modalActive: false,
            selectedProducts: selectedProducts
        }, () => this.loadPreview());
    }

    hasErrors = (errors) => {
        return Object.getOwnPropertyNames(errors).length > 0;
    }

    loadPreview = () => {

        this.previewTimer = null;

        if (this.state.selectedProducts.length === 0 || this.hasErrors(this.state.ruleErrors)) {
            return;
        }

        this.setState({
            loadingPreview: true
        });

        const preview = {
            ...this.state.rule,
            previewIds: this.state.selectedProducts.map(_ => _.id),
        };

        let body = JSON.stringify({ ...preview });

        fetch(`api/tagRules/${this.ruleDefinition.ruleType}/preview`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: body
        }).then(response => response.json())
            .then(data => this.setState({
                resourcePreviews: data,
                loadingPreview: false
            }));
    }

    renderPreviewArea = () => {

        if (this.state.loadingPreview) {
            return <Card.Section><Spinner size="large" /></Card.Section>
        }

        return <Card.Section>
            <TagRulePreviewArea
                resources={this.state.selectedProducts}
                resourcePreviews={this.state.resourcePreviews}
                resourceType={this.ruleDefinition.resourceType}
            />
        </Card.Section>
    }

    handleSaveOrCreate = () => {

        let body = JSON.stringify({ ...this.state.rule });
        let method = "POST";
        let url = `api/tagRules`;

        if (this.editId != null) {
            method = "PUT";
            url = `api/tagRules/${this.editId}`
            body = JSON.stringify({
                ...this.state.rule,
            });
        }

        fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
            },
            body: body
        }).then(() => {
            this.props.history.push("/tag-rules");
        });
    }

    fetchRuleInfoData = () => {

        if (this.editId == null) {
            this.setState({
                loading: false
            });
            return;
        }

        fetch(`api/tagRules/info/${this.editId}`, {
            method: 'get'
        })
            .then(response => response.json())
            .then(data => {
                this.initRule(data.type);
                this.fetchRule();
            });
    }

    fetchRule = () => {
        if (this.editId != null) {
            fetch(`api/tagRules/info/${this.editId}`, {
                method: 'get'
            })
                .then(response => response.json())
                .then(data => this.setState({
                    rule: data,
                    ruleErrors: this.ruleValidator.validate(data),
                    loading: false
                }));
        }
        else {
            this.setState({
                loading: false
            })
        }
    }

    componentDidMount() {
        this.fetchRuleInfoData();
    }
}

export default TagRulePage;